import { useCallback, useEffect, useMemo, useRef } from 'react';
import { IToggleControls } from '~/hooks/useToggle';
import { ErrorType, TInternalValue } from './Types';
import { calculateDurationInMinutes, formatDuration, formatTimeLimit } from './Utils';

interface IUseTimeLimitRestrictionProps {
  controls: IToggleControls;
  internalValues: TInternalValue;
  timeLimitInMinutes?: number;
}

export const useTimeLimitValidation = ({
  controls,
  internalValues,
  timeLimitInMinutes,
}: IUseTimeLimitRestrictionProps) => {
  const hasError = useRef(false);

  const message = useMemo(() => {
    if (!timeLimitInMinutes) {
      return '';
    }
    // eslint-disable-next-line max-len
    return `Exceeds ${formatTimeLimit(timeLimitInMinutes)} limit (currently ${formatDuration(calculateDurationInMinutes(internalValues.arrival, internalValues.departure))}). Update either time to proceed.`;
  }, [internalValues.arrival, internalValues.departure, timeLimitInMinutes]);

  const doValidate = useCallback(
    (newArrival: string, newDeparture: string): ErrorType | undefined => {
      if (timeLimitInMinutes) {
        const duration = calculateDurationInMinutes(newArrival, newDeparture);
        if (duration > timeLimitInMinutes) {
          controls.setOn();
          hasError.current = true;
          return ErrorType.ExceedsTimeLimit;
        }
      }

      if (hasError.current) {
        controls.setOff();
      }

      hasError.current = false;
      return undefined;
    },
    [controls, timeLimitInMinutes],
  );

  useEffect(() => {
    if (timeLimitInMinutes && hasError.current) {
      doValidate(internalValues.arrival, internalValues.departure);
    }
  }, [doValidate, internalValues.arrival, internalValues.departure, timeLimitInMinutes]);

  return {
    message,
    doValidate,
  };
};
